import React, {createContext, useContext, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch} from 'react-redux';
import * as fileActions from '../_redux/actions/OrderFilesActions';
import {useOrderDetailsUIGlobalContext} from './OrderDetailsUIGlobalContext';
import {useFileTypesOptionsContext} from '../../_common/components/FileTypesOptionsContext/FileTypesOptionsContext';
import AnimatedUploadingFilePopUp from '../../_common/components/AnimatedUploadingFilePopUp';
import {showGlobalMessage} from '../../_common/components/GlobalMessage/actions/showGlobalMessage';
import {messages as M} from '../../../../_metronic/i18n/messages';

export type OrderFileData = {
  fileID: number
  orderID: number,
  fileName: string
  fileExtension: string
  uploadTime: string
  orderFileTypeID: number
  fileTypeID: number
  fileTypeName: string
  folderID: number
  folderName: string
  uploadedByUserID: number
  uploadedByUserFirstName: string
  uploadedByUserLastName: string
  link: string
}

type OrderDetailsUIFilesContext = {
  fileList: OrderFileData[],
  uploadFiles: (files: File[], fileTypeID: number) => Promise<void>,
  handleDownloadFile: (fileName: string) => void,
  handleDeleteFile: (fileID: number, fileName: string) => void,
  handleUpdateFileType: (fileID: number, fileTypeID: number) => void,
  handleViewFile: (fileName: string) => void,
  fileTypesOptions: {[key: string]: unknown}[],
  updateEditedFile: (fileID: number, file: Blob) => void
  updateFileList: () => void
  loading: boolean
}

const OrderDetailsUIFilesContext = createContext<OrderDetailsUIFilesContext>({} as OrderDetailsUIFilesContext);

export function useOrderDetailsUIFilesContext() {
  return useContext(OrderDetailsUIFilesContext);
}

interface OrderDetailsUIFilesProviderProps {
  children: React.ReactNode;
}

export function OrderDetailsUIFilesProvider({children}: OrderDetailsUIFilesProviderProps) {
  const dispatch = useDispatch();
  const {orderID} = useOrderDetailsUIGlobalContext() as unknown as {orderID: number};
  const [uploadingFile, setUploadingFile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState<OrderFileData[]>([]);

  const updateFileList = async () => {
    setLoading(true);
    const result = await dispatch<any>(fileActions.getFileList(orderID));
    const array = Array.isArray(result) ? result : [];
    setFileList(array);
    if (result) {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orderID &&  orderID !== -1) {
      updateFileList();
    }
  }, []);

  const onUploadFile = async (files: File[], fileTypeID: number) => {
    setUploadingFile(true);
    const calls = files.map(async (item) => await fileActions.uploadFile(item, orderID, fileTypeID, item.name));
    const results = await Promise.all(calls);
    if (results) {
      await updateFileList();
      document.body.click();
    }
    dispatch(showGlobalMessage(
      results ? 'success' : 'error',
      M[results ? '0' : '-1'])
    )
    setTimeout(() => setUploadingFile(false), 1000);
  };

  const handleDownloadFile = (fileName: string) => {
    if (orderID) {
      dispatch(fileActions.getFile({orderID, fileName}));
    }
  };
  const handleViewFile = (fileName: string) => {
    if (orderID) {
      return fileActions.getFilePreview({orderID, fileName});
    }
  };
  const handleDeleteFile = async (fileID: number, fileName: string) => {
    dispatch<any>(fileActions.deleteFile({orderID, fileID, fileName}))
      .then(() => updateFileList());
  };
  const handleUpdateFileType = async (fileID: number, fileTypeID: number) => {
    await fileActions.updateFileType(fileID, fileTypeID);
    await updateFileList();
  };

  const {fileTypes: fileTypesOptions} = useFileTypesOptionsContext();

  const updateEditedFile = async (fileID: number, file: Blob) => {
    const [fileData] = fileList.filter((file) => file.fileID === fileID);
    if (fileData) {
      await dispatch<any>(fileActions.deleteFile({orderID, fileID, fileName: fileData.fileName}));
      await fileActions.uploadFile(file, orderID, fileData.fileTypeID, fileData.fileName);
    }
  };

  return (
    <>
      <AnimatedUploadingFilePopUp show={uploadingFile}/>
      <OrderDetailsUIFilesContext.Provider value={{
        fileList,
        uploadFiles: onUploadFile,
        handleDownloadFile,
        handleDeleteFile,
        handleUpdateFileType,
        handleViewFile,
        fileTypesOptions,
        updateEditedFile,
        updateFileList,
        loading
      }}>
        {children}
      </OrderDetailsUIFilesContext.Provider>
    </>
  );
}

OrderDetailsUIFilesProvider.propTypes = {
  children: PropTypes.node,
};